/* __next_internal_client_entry_do_not_use__ default auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Global
import { tv } from 'tailwind-variants';
import { useEffect } from 'react';
// Local
import { SvgIcon } from 'helpers/SvgIcon';
import useDictionary from 'lib/hooks/useDictionary';
import { useIsScrolled } from 'lib/hooks/useIsScrolled';
import { getTestProps } from 'lib/testing/utils';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';
const tailwindVariants = tv({
    slots: {
        base: [
            'fixed',
            'bottom-6',
            'right-6',
            'z-40',
            'flex',
            'items-center',
            'gap-4',
            'group',
            'transition-opacity',
            'duration-300',
            'ease-in-out'
        ],
        button: [
            'flex',
            'items-center',
            'justify-center',
            'w-11',
            'h-11',
            'md:w-14',
            'md:h-14',
            'bg-component-button-tonal-filled-bg',
            'text-component-button-tonal-filled-text',
            'border-general-border-width-button',
            'border-component-button-tonal-filled-border',
            'rounded-full',
            'shadow-lg',
            'hover:bg-component-button-tonal-filled-bg-hover',
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-blue-500',
            'focus:ring-offset-1',
            'disabled:bg-component-button-tonal-filled-bg-disabled',
            'transition-colors',
            'duration-200',
            'bg-white',
            'hover:bg-color-grayscale-200',
            'group'
        ],
        label: [
            'hidden',
            'bg-color-bg-light',
            'text-color-text-primary',
            'duration-300',
            'md:flex',
            'font-semibold',
            'items-center',
            'justify-center',
            'min-h-8',
            'md:min-h-8',
            'mt-0',
            'w-0',
            'order-first',
            'overflow-hidden',
            'py-spacing-padding-large-1',
            'rounded-general-border-radius-button',
            'transition-[width,padding]',
            'whitespace-nowrap',
            'group-hover:px-4',
            'group-hover:w-auto',
            'group-focus:px-4',
            'group-focus:w-auto'
        ]
    },
    variants: {
        isVisible: {
            true: {
                base: [
                    'opacity-100',
                    'pointer-events-auto'
                ]
            },
            false: {
                base: [
                    'opacity-0',
                    'pointer-events-none'
                ]
            }
        }
    }
});
const BackToTop = ()=>{
    const isEditing = useIsEditing();
    const isScrolled = useIsScrolled();
    const { getDictionaryValue } = useDictionary();
    const fallbackText = 'Back to top';
    const scrollToTop = ()=>{
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };
    // Move chat widget up when back-to-top button is visible
    useEffect(()=>{
        const chatWidget = document.getElementById('Microsoft_Omnichannel_LCWidget_Chat_Iframe_Window');
        if (!chatWidget) return;
        if (isScrolled) {
            // Move chat widget up to avoid overlapping with back-to-top button
            // Back-to-top button: bottom-6 (24px) on both mobile and desktop, height 44px/56px
            // Chat widget needs to be above: ~90px on both mobile and desktop
            chatWidget.style.bottom = '90px';
            chatWidget.style.transition = 'bottom 300ms ease-in-out';
        } else {
            // Reset to default position
            chatWidget.style.bottom = '20px';
            chatWidget.style.transition = 'bottom 300ms ease-in-out';
        }
    }, [
        isScrolled
    ]);
    const { base, button, label } = tailwindVariants({
        isVisible: isScrolled
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: base(),
        "data-component": "authorable/shared/site-structure/backtotop",
        ...editingHydrationProps(isEditing),
        ...getTestProps(`component-back-to-top`),
        children: [
            /*#__PURE__*/ _jsxs("button", {
                className: button(),
                onClick: scrollToTop,
                "aria-label": getDictionaryValue('BackToTopText') || fallbackText,
                "aria-hidden": !isScrolled,
                disabled: !isScrolled,
                type: "button",
                ...getTestProps(`back-to-top-button`),
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "sr-only",
                        children: getDictionaryValue('BackToTopText') || fallbackText
                    }),
                    /*#__PURE__*/ _jsx(SvgIcon, {
                        icon: "chevron-up",
                        size: "xs"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("span", {
                className: label(),
                children: getDictionaryValue('BackToTopText') || fallbackText
            })
        ]
    });
};
export default BackToTop;
